import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../Auth/AuthContext";
import BASE_URL from "../../../config/config";
import "../../../css/UserCourse.module.css";
import HomeSection from "../../template/HomeSection";

// --- کامپوننت کارت دوره ---
const CourseCard = ({ course }) => (
  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
    {/* Image Placeholder */}
    <div className="bg-primary/20 h-40 flex items-center justify-center">
      <span className="text-6xl">{course.icon || "📚"}</span>
    </div>

    {/* Content */}
    <div className="p-6">
      <h3 className="text-xl font-bold text-dark mb-2 truncate">{course.title}</h3>
      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{course.description}</p>

      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-primary font-bold">{course.levelName}</span>
        <span className="text-sm text-gray-500">{course.duration} ویدیو</span>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-lg font-bold text-primary">
          {course.price === "رایگان" ? "رایگان" : `${course.price} تومان`}
        </span>
        {course.oldPrice && (
          <span className="text-sm text-gray-500 line-through">{course.oldPrice} تومان</span>
        )}
      </div>
    </div>

    {/* Button */}
    <div className="p-6 pt-0">
      <Link to={`/courses/${course.id}`}>
        <button className="w-full bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-dark transition">
          🎓 مشاهده دوره
        </button>
      </Link>
    </div>
  </div>
);

// --- کامپوننت اصلی ---
const UserCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("completed"); // تب فعال

  useEffect(() => {
    const fetchUserCourses = async () => {
      try {
        if (!user) {
          navigate("/login");
          return;
        }

        const response = await fetch(`${BASE_URL}/user/courses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "دریافت دوره‌ها با خطا مواجه شد.");
        }

        const data = await response.json();
        if (data.status === "success" && Array.isArray(data.data)) {
          setCourses(data.data);
        } else {
          setCourses([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserCourses();
  }, [user, navigate]);

  // تفکیک دوره‌ها
  const completedCourses = courses.filter((c) => c.status === "completed");
  const pendingCourses = courses.filter((c) => c.status === "pending");

  // تب‌های نمایش
  const renderTabContent = () => {
    const currentCourses =
      activeTab === "completed" ? completedCourses : pendingCourses;

    if (currentCourses.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          هیچ دوره‌ای در این دسته یافت نشد.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {currentCourses.map((course) => (
          <CourseCard
            key={course.id}
            course={{
              id: course.id,
              title: course.title,
              description: course.short_description || "این دوره شامل توضیحاتی است.",
              levelName: course.level === "beginner" ? "مقدماتی" : course.level === "intermediate" ? "متوسط" : "پیشرفته",
              price: course.price === "0" ? "رایگان" : `${course.price} تومان`,
              duration: course.total_duration || "نامشخص",
              icon: "🎓",
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-light min-h-screen font-vazirmatn">
      {/* Breadcrumb */}
     
      {loading && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary"></div>
            <p className="mt-4 text-gray-600">در حال بارگذاری دوره‌ها...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center text-red-600">{error}</div>
        </div>
      )}

      {!loading && !error && courses.length === 0 && (
        <div className="container mx-auto px-4 py-12">
          <div className="text-center text-gray-600">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-dark mb-2">هیچ دوره‌ای یافت نشد</h3>
            <p>شما هنوز در دوره‌ای ثبت‌نام نکرده‌اید.</p>
            <Link to="/courses" className="mt-6 inline-block bg-primary text-white px-6 py-3 rounded-xl font-bold">
              👉 مشاهده دوره‌ها
            </Link>
          </div>
        </div>
      )}

      {!loading && !error && courses.length > 0 && (
        <div className="container mx-auto px-4 py-12">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-6 py-3 font-bold transition ${
                activeTab === "completed"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              ✅ تکمیل شده ({completedCourses.length})
            </button>
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-6 py-3 font-bold transition ${
                activeTab === "pending"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              ⏳ در انتظار تکمیل ({pendingCourses.length})
            </button>
          </div>

          {/* Tab Content */}
          <div>{renderTabContent()}</div>
        </div>
      )}
    </div>
  );
};

export default UserCourses;